import { useEffect, useState } from "react";
import { Upload, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, PageTitle, fmtMoney, Th, Td, Modal, inputCls, labelCls, btnPrimary, btnGhost } from "@/admin/ui";

const EMPTY = {
  name: "", price: "", collection: "pulse", gender: "unisex", product_type: "",
  description: "", fabric: "", fit: "", sizes: "S, M, L, XL", colors: "Black|#131417",
  images: "", featured: false, best_seller: false, final_sale: false, production_days: 0, production_min_days: "",
};

function toForm(p) {
  return {
    name: p.name, price: String(p.price), collection: p.collection, gender: p.gender || "unisex",
    product_type: p.product_type || "", description: p.description || "", fabric: p.fabric || "",
    fit: p.fit || "", sizes: (p.sizes || []).join(", "),
    colors: (p.colors || []).map((c) => `${c.name}|${c.hex}`).join(", "),
    images: (p.images || []).join("\n"), featured: !!p.featured, best_seller: !!p.best_seller, final_sale: !!p.final_sale,
    production_days: p.production_days || 0, production_min_days: p.production_min_days ?? "",
  };
}

function toPayload(f) {
  return {
    name: f.name.trim(), price: parseFloat(f.price), collection: f.collection, gender: f.gender,
    product_type: f.product_type.trim() || null, description: f.description, fabric: f.fabric, fit: f.fit,
    sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
    colors: f.colors.split(",").map((c) => {
      const [name, hex] = c.split("|").map((x) => x.trim());
      return { name, hex: hex || "#131417" };
    }).filter((c) => c.name),
    images: f.images.split("\n").map((s) => s.trim()).filter(Boolean),
    featured: f.featured, best_seller: f.best_seller, final_sale: f.final_sale,
    production_days: parseInt(f.production_days, 10) || 0,
    production_min_days: f.production_min_days === "" ? null : parseInt(f.production_min_days, 10),
    accessory_type: f.collection === "accessories" ? "gear" : null,
  };
}

function ProductForm({ initial, editingId, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const imageList = f.images.split("\n").map((s) => s.trim()).filter(Boolean);

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const urls = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/admin/upload-image", fd);
        urls.push(data.url);
      } catch (err) {
        toast.error(`Could not upload ${file.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    if (urls.length) {
      setF((prev) => ({ ...prev, images: [prev.images.trim(), ...urls].filter(Boolean).join("\n") }));
      toast.success(`${urls.length} photo${urls.length === 1 ? "" : "s"} uploaded.`);
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (src) =>
    setF((prev) => ({
      ...prev,
      images: prev.images.split("\n").map((s) => s.trim()).filter((s) => s && s !== src).join("\n"),
    }));

  const save = async (e) => {
    e.preventDefault();
    const payload = toPayload(f);
    if (!payload.name || !payload.price || payload.price <= 0) { toast.error("Name and a valid price are required."); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/admin/products/${editingId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editingId ? "Product updated." : "Product created.");
      onSaved();
    } catch { toast.error("Could not save product."); }
    setSaving(false);
  };

  return (
    <Modal open onClose={onClose} wide>
      <form onSubmit={save} data-testid="product-form">
        <h2 className="font-anton uppercase text-2xl mb-6">{editingId ? "Edit Product" : "New Product"}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Name *</label><input value={f.name} onChange={set("name")} className={inputCls} required data-testid="pf-name" /></div>
          <div><label className={labelCls}>Price (USD) *</label><input type="number" step="0.01" min="0.01" value={f.price} onChange={set("price")} className={inputCls} required data-testid="pf-price" /></div>
          <div>
            <label className={labelCls}>Collection</label>
            <select value={f.collection} onChange={set("collection")} className={`${inputCls} bg-ink`} data-testid="pf-collection">
              <option value="pulse">PULSE — Performance</option>
              <option value="core">CORE — Casual</option>
              <option value="accessories">Accessories</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Gender</label>
            <select value={f.gender} onChange={set("gender")} className={`${inputCls} bg-ink`} data-testid="pf-gender">
              <option value="unisex">Unisex</option><option value="men">Men</option><option value="women">Women</option>
            </select>
          </div>
          <div className="sm:col-span-2"><label className={labelCls}>Category / Product type</label>
            <input value={f.product_type} onChange={set("product_type")} className={inputCls} placeholder="e.g. Men's Performance / Regular Fit Tops" data-testid="pf-type" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Description</label>
            <textarea value={f.description} onChange={set("description")} rows={4} className={inputCls} data-testid="pf-description" /></div>
          <div><label className={labelCls}>Fabric & care</label><input value={f.fabric} onChange={set("fabric")} className={inputCls} data-testid="pf-fabric" /></div>
          <div><label className={labelCls}>Fit</label><input value={f.fit} onChange={set("fit")} className={inputCls} data-testid="pf-fit" /></div>
          <div><label className={labelCls}>Sizes (comma separated)</label><input value={f.sizes} onChange={set("sizes")} className={inputCls} data-testid="pf-sizes" /></div>
          <div><label className={labelCls}>Colors (Name|#hex, comma separated)</label><input value={f.colors} onChange={set("colors")} className={inputCls} data-testid="pf-colors" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Product photos</label>
            <div className="flex items-center gap-3 mb-2">
              <label className={`${btnGhost} cursor-pointer inline-flex items-center gap-2 ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="pf-upload-label">
                <Upload size={13} /> {uploading ? "Uploading…" : "Upload Photos"}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden"
                  onChange={uploadImages} disabled={uploading} data-testid="pf-upload-input" />
              </label>
              <span className="font-mono text-[10px] text-graphite">JPG / PNG / WEBP · up to 8MB each</span>
            </div>
            {imageList.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="pf-image-previews">
                {imageList.map((src) => (
                  <div key={src} className="relative w-16 h-20 bg-white border border-border group">
                    <img src={src} alt="" className="w-full h-full object-contain" />
                    <button type="button" onClick={() => removeImage(src)} aria-label="Remove image"
                      data-testid={`pf-image-remove-${imageList.indexOf(src)}`}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-ink border border-border text-chalk/80 hover:text-pulse hover:border-pulse flex items-center justify-center">
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className={labelCls}>Image URLs (one per line)</label>
            <textarea value={f.images} onChange={set("images")} rows={3} className={inputCls} placeholder="/products/my-product-1.png or https://…" data-testid="pf-images" /></div>
          <div><label className={labelCls}>Production days (max)</label><input type="number" min="0" value={f.production_days} onChange={set("production_days")} className={inputCls} data-testid="pf-proddays" /></div>
          <div><label className={labelCls}>Production days (min, optional)</label><input type="number" min="0" value={f.production_min_days} onChange={set("production_min_days")} className={inputCls} data-testid="pf-prodmin" /></div>
          <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider">
            <input type="checkbox" checked={f.featured} onChange={set("featured")} data-testid="pf-featured" style={{ accentColor: "rgb(var(--signal))" }} /> Featured
          </label>
          <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider">
            <input type="checkbox" checked={f.best_seller} onChange={set("best_seller")} data-testid="pf-bestseller" style={{ accentColor: "rgb(var(--signal))" }} /> Best seller
          </label>
          <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider">
            <input type="checkbox" checked={f.final_sale} onChange={set("final_sale")} data-testid="pf-finalsale" style={{ accentColor: "rgb(var(--signal))" }} /> Final sale (non-returnable)
          </label>
        </div>
        <div className="flex gap-3 mt-6">
          <button type="submit" disabled={saving} className={btnPrimary} data-testid="pf-save">{saving ? "Saving…" : editingId ? "Save Changes" : "Create Product"}</button>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
        </div>
        <p className="font-mono text-[10px] text-graphite mt-3">
          Price changes sync to Stripe automatically. Advanced fields (size guide, per-color photos, SKUs) are preserved on edit.
        </p>
      </form>
    </Modal>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null); // {initial, editingId}

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This removes it from the store immediately.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted.");
      load();
    } catch { toast.error("Could not delete product."); }
  };

  return (
    <div data-testid="admin-products-page">
      <PageTitle title="Products" sub={`${products.length} products in the catalog.`}>
        <button onClick={() => setForm({ initial: EMPTY, editingId: null })} className={btnPrimary} data-testid="product-add-btn">
          + Add Product
        </button>
      </PageTitle>
      <Card className="overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead className="border-b border-border">
            <tr><Th></Th><Th>Product</Th><Th>Price</Th><Th>Collection</Th><Th>Sizes</Th><Th>Flags</Th><Th className="text-right">Actions</Th></tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-border/40" data-testid={`product-row-${p.id}`}>
                <Td className="w-16"><div className="w-12 h-14 bg-white overflow-hidden"><img src={p.images?.[0]} alt="" className="w-full h-full object-contain" /></div></Td>
                <Td>
                  <p className="text-sm">{p.name}</p>
                  <p className="font-mono text-[10px] text-graphite">{p.id}</p>
                </Td>
                <Td className="font-mono text-xs">{fmtMoney(p.price)}</Td>
                <Td className="font-mono text-[10px] uppercase tracking-wider text-graphite">{p.collection}</Td>
                <Td className="font-mono text-[10px] text-graphite">{(p.sizes || []).join(" ")}</Td>
                <Td className="font-mono text-[9px] uppercase tracking-wider">
                  {p.featured && <span className="text-signal mr-2">Featured</span>}
                  {p.best_seller && <span className="text-pulse">Best seller</span>}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  <button onClick={() => setForm({ initial: toForm(p), editingId: p.id })} data-testid={`product-edit-${p.id}`}
                    className="font-mono text-[10px] uppercase tracking-wider border border-border px-3 py-1.5 hover:border-chalk mr-2">Edit</button>
                  <button onClick={() => remove(p)} data-testid={`product-delete-${p.id}`}
                    className="font-mono text-[10px] uppercase tracking-wider border border-pulse/50 text-pulse px-3 py-1.5 hover:bg-pulse hover:text-ink">Delete</button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {form && (
        <ProductForm initial={form.initial} editingId={form.editingId}
          onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      )}
    </div>
  );
}
